import type { ColumnDef } from '@tanstack/react-table';
import { CircleCheck } from 'lucide-react';
import { DateCell } from '@/components/data-table/cells';
import { StatusChip } from '@/components/ui/status-chip';
import { MessageActions } from '@/features/notifications/message-actions';
import { STATUS_CHIP_VARIANTS, STATUS_LABELS } from '@/features/notifications/notifications-meta';
import type { NotificationMessage } from '@/services/api/notifications';

interface MessageColumnDeps {
  approvers: string[];
  onAmend: (message: NotificationMessage) => void;
}

// Sorting is disabled everywhere: the list is cursor-paginated and server-sorted (newest first)
export const buildMessageColumns = ({ approvers, onAmend }: MessageColumnDeps): ColumnDef<NotificationMessage, unknown>[] => [
  {
    accessorKey: 'title',
    header: 'Message',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="max-w-md min-w-0">
        <p className="truncate font-medium">{row.original.title}</p>
        <p className="truncate text-xs text-muted-foreground">{row.original.body}</p>
        {row.original.reviewComments ? <p className="truncate text-xs text-amber-500">Returned: {row.original.reviewComments}</p> : null}
      </div>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    enableSorting: false,
    cell: ({ row }) => (
      <StatusChip className="px-2 py-0.5 text-xs" variant={STATUS_CHIP_VARIANTS[row.original.status]}>
        {STATUS_LABELS[row.original.status]}
        {row.original.status === 'SENT' && row.original.sentWithoutLegalReview ? ' (no legal review)' : ''}
      </StatusChip>
    ),
  },
  {
    accessorKey: 'legalApprovedBy',
    header: 'Legal',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.legalApprovedBy ? (
        <span className="inline-flex items-center gap-1 text-emerald-500" title={`Approved by ${row.original.legalApprovedBy}`}>
          <CircleCheck aria-hidden className="size-4" />
          <span className="text-xs">Approved</span>
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
  },
  {
    accessorKey: 'approverEmail',
    header: 'Approver',
    enableSorting: false,
    cell: ({ row }) => <span className="whitespace-nowrap text-xs">{row.original.approverEmail ?? '—'}</span>,
  },
  {
    accessorKey: 'createdBy',
    header: 'Created By',
    enableSorting: false,
    cell: ({ row }) => <span className="whitespace-nowrap text-xs">{row.original.createdBy}</span>,
  },
  {
    accessorKey: 'updatedAt',
    header: 'Updated',
    enableSorting: false,
    cell: ({ row }) => <DateCell value={row.original.updatedAt} />,
  },
  {
    id: 'actions',
    header: '',
    enableSorting: false,
    cell: ({ row }) => <MessageActions approvers={approvers} message={row.original} onAmend={onAmend} />,
  },
];
